import React, { useState, useEffect, useRef } from 'react';
import { Menu, X, Home, Calculator, Mail } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Modal from './Modal';
import ContactForm from './ContactForm';

export default function Navbar() {
  const [isOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const navigate = useNavigate();
  const pressTimer = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleMouseDown = () => {
    pressTimer.current = setTimeout(() => {
      navigate('/admin');
    }, 4000);
  };

  const handleMouseUp = () => {
    if (pressTimer.current) {
      clearTimeout(pressTimer.current);
      pressTimer.current = null;
    }
  };

  const handleTouchStart = () => {
    pressTimer.current = setTimeout(() => {
      navigate('/admin');
    }, 4000);
  };

  const handleTouchEnd = () => {
    if (pressTimer.current) {
      clearTimeout(pressTimer.current);
      pressTimer.current = null;
    }
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
    }
  };

  return (
    <>
      <nav className={`glass-navbar ${scrolled ? 'shadow-lg' : ''}`}>
        <div className="flex justify-between items-center h-10 sm:h-14">
          {/* Menu Button (Left) */}
          <button
            onClick={() => setIsMenuOpen(!isOpen)}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            className="p-1 sm:p-2 rounded-lg hover:bg-[#2A4365]/5 transition-colors"
            aria-label="Menu principal"
          >
            <Menu className="h-4 w-4 sm:h-5 sm:w-5 text-[#2A4365]" strokeWidth={2.5} />
          </button>

          {/* Logo (Center) */}
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
            <img 
              src="https://studio.estimhabitat.com/storage/v1/object/public/app-img//EstimHabitat_logo_mini.png"
              alt="ESTIM HABITAT Logo" 
              className="h-6 sm:h-10 w-auto"
            />
          </div>

          {/* Contact Button (Right) */}
          <button
            onClick={() => setShowContactForm(true)}
            className="px-2 py-1 sm:px-4 sm:py-2 text-xs sm:text-sm font-medium rounded-lg text-white
              bg-gradient-to-r from-[#2A4365] via-[#4A5568] to-[#C4A052]
              hover:from-[#1A3355] hover:via-[#3A4558] hover:to-[#B49042]
              focus:outline-none focus:ring-2 focus:ring-[#2A4365] focus:ring-offset-2
              transform transition-all duration-300 hover:scale-105
              shadow-lg hover:shadow-xl hover:shadow-[#2A4365]/20"
          >
            Nous contacter
          </button>
        </div>

        {/* Mobile menu */}
        {isOpen && (
          <div 
            className="fixed inset-0 bg-black/20 backdrop-blur-sm z-20"
            onClick={() => setIsMenuOpen(false)}
          >
            <div 
              className="absolute left-4 top-20 w-64 bg-white/80 backdrop-blur-xl rounded-xl shadow-[0_8px_32px_rgba(42,67,101,0.15)] border border-white/20 overflow-hidden"
              onClick={e => e.stopPropagation()}
            >
              <div className="p-4 border-b border-white/10">
                <h2 className="text-lg font-bold text-gray-900">
                  Menu
                </h2>
              </div>

              <div className="p-2">
                <button
                  onClick={() => scrollToSection('accueil')}
                  className="w-full flex items-center px-4 py-3 text-gray-900 font-bold hover:bg-white/50 rounded-lg transition-colors"
                >
                  <Home className="h-5 w-5 mr-3 text-[#2A4365]" strokeWidth={2.5} />
                  <span>Accueil</span>
                </button>

                <button
                  onClick={() => scrollToSection('estimation-rapide')}
                  className="w-full flex items-center px-4 py-3 text-gray-900 font-bold hover:bg-white/50 rounded-lg transition-colors"
                >
                  <Calculator className="h-5 w-5 mr-3 text-[#2A4365]" strokeWidth={2.5} />
                  <span>Estimation</span>
                </button>

                <button
                  onClick={() => {
                    setIsMenuOpen(false);
                    setShowContactForm(true);
                  }}
                  className="w-full flex items-center px-4 py-3 text-gray-900 font-bold hover:bg-white/50 rounded-lg transition-colors"
                >
                  <Mail className="h-5 w-5 mr-3 text-[#2A4365]" strokeWidth={2.5} />
                  <span>Contact</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </nav>

      <Modal isOpen={showContactForm} onClose={() => setShowContactForm(false)}>
        <ContactForm onClose={() => setShowContactForm(false)} />
      </Modal>
    </>
  );
}